import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';

import { type ConversationAction } from '../conversation-reducer';

export const useHandoffPolling = ({
  dispatch,
  setIsSessionClosed,
}: {
  dispatch: Dispatch<ConversationAction>;
  setIsSessionClosed: Dispatch<SetStateAction<boolean>>;
}) => {
  const handoffPollRef = useRef<number | null>(null);
  const knownHandoffIdsRef = useRef<Set<string>>(new Set());

  const stopHandoffPolling = () => {
    if (handoffPollRef.current !== null) {
      window.clearInterval(handoffPollRef.current);
      handoffPollRef.current = null;
    }
  };

  const pollHandoffMessages = async (handoffSessionId: string, seedOnly = false) => {
    try {
      const res = await fetch(`/api/handoff/${handoffSessionId}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const sessionMessages = await res.json();
      if (!Array.isArray(sessionMessages)) return;
      let sawResolved = false;
      for (const sessionMessage of sessionMessages) {
        const id = String(sessionMessage?.id ?? '');
        if (sessionMessage?.resolved === true) sawResolved = true;
        if (!id || knownHandoffIdsRef.current.has(id)) continue;
        knownHandoffIdsRef.current.add(id);
        if (!seedOnly && sessionMessage.role === 'agent') {
          dispatch({
            type: 'note/appended',
            message: {
              id: `agent-${id}`,
              role: 'assistant',
              content: String(sessionMessage.content ?? ''),
              createdAt: String(sessionMessage.createdAt ?? new Date().toISOString()),
              status: 'success',
            },
          });
        }
      }
      if (sawResolved) {
        setIsSessionClosed(true);
        stopHandoffPolling();
      }
    } catch {}
  };

  const startHandoffPolling = (handoffSessionId: string) => {
    if (handoffPollRef.current !== null) return;
    knownHandoffIdsRef.current = new Set();
    void pollHandoffMessages(handoffSessionId, true).then(() => {
      handoffPollRef.current = window.setInterval(() => {
        if (document.hidden) return;
        void pollHandoffMessages(handoffSessionId);
      }, 4000);
    });
  };

  useEffect(() => {
    return () => {
      stopHandoffPolling();
    };
  }, []);

  return { startHandoffPolling, stopHandoffPolling, knownHandoffIdsRef };
};
